import { zodResolver } from '@hookform/resolvers/zod';
import { LoginInput } from '@tarja/contracts';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, TextField } from '@tarja/ui';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ApiError } from '../api/client';
import { api } from '../api/tarja';
import { useAuth } from '../auth/AuthProvider';

// Mismo esquema que el API (contracts), con mensajes en español para la interfaz.
const EsquemaLogin = LoginInput.extend({
  login: z.string().trim().min(3, 'Ingresa tu usuario (al menos 3 caracteres)').max(200),
  password: z.string().min(1, 'Ingresa tu contraseña').max(200),
});
const EsquemaMfa = z.object({ codigo: z.string().trim().regex(/^\d{6}$/, 'El código tiene 6 dígitos') });

function FormLogin({ onOlvido }: { onOlvido: () => void }) {
  const { login } = useAuth();
  const form = useForm<z.infer<typeof EsquemaLogin>>({ resolver: zodResolver(EsquemaLogin), defaultValues: { login: '', password: '' } });
  const [error, setError] = useState<string | null>(null);
  const enviar = form.handleSubmit(async (d) => {
    setError(null);
    try {
      await login(d.login, d.password);
    } catch (e) {
      setError(e instanceof ApiError ? e.mensaje : 'No se pudo iniciar sesión.');
    }
  });
  return (
    <form onSubmit={enviar} noValidate aria-describedby={error ? 'error-login' : undefined}>
      <TextField label="Usuario" autoComplete="username" required error={form.formState.errors.login?.message} {...form.register('login')} />
      <TextField label="Contraseña" type="password" autoComplete="current-password" required error={form.formState.errors.password?.message} {...form.register('password')} />
      {error ? (
        <div id="error-login">
          <Alert tono="peligro">{error}</Alert>
        </div>
      ) : null}
      <div className="t-acciones">
        <Button type="submit" variante="primario" cargando={form.formState.isSubmitting}>
          Ingresar
        </Button>
        <Button variante="fantasma" onClick={onOlvido}>
          Olvidé mi contraseña
        </Button>
      </div>
    </form>
  );
}

function FormMfa() {
  const { verificarMfa, cancelarMfa, enrolamientoMfa } = useAuth();
  const form = useForm<z.infer<typeof EsquemaMfa>>({ resolver: zodResolver(EsquemaMfa), defaultValues: { codigo: '' } });
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => ref.current?.focus(), []);
  const { ref: regRef, ...reg } = form.register('codigo');
  const enviar = form.handleSubmit(async (d) => {
    setError(null);
    try {
      await verificarMfa(d.codigo);
    } catch (e) {
      setError(e instanceof ApiError ? (e.code === 'AUTH_CREDENCIALES' || e.status === 401 ? 'El código no es válido o expiró.' : e.mensaje) : 'No se pudo verificar el código.');
      form.setValue('codigo', '');
    }
  });
  return (
    <form onSubmit={enviar} noValidate>
      {enrolamientoMfa ? (
        <Alert tono="info" titulo="Configura tu aplicación de autenticación">
          <p>
            Es tu primer ingreso con verificación en dos pasos. Agrega esta cuenta en tu app (Google Authenticator, Microsoft Authenticator u otra) con la
            clave:
          </p>
          <p className="t-mono" aria-label="Clave TOTP">
            {enrolamientoMfa.secreto.replace(/(.{4})/g, '$1 ').trim()}
          </p>
          <p>
            <a href={enrolamientoMfa.otpauth_url}>Abrir en la app de autenticación</a>
          </p>
        </Alert>
      ) : null}
      <p>Ingresa el código de 6 dígitos de tu aplicación de autenticación.</p>
      <TextField
        label="Código de verificación"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        className="w-codigo-mfa"
        required
        error={form.formState.errors.codigo?.message}
        ref={(el) => {
          regRef(el);
          ref.current = el;
        }}
        {...reg}
      />
      {error ? <Alert tono="peligro">{error}</Alert> : null}
      <div className="t-acciones">
        <Button type="submit" variante="primario" cargando={form.formState.isSubmitting}>
          Verificar
        </Button>
        <Button variante="fantasma" onClick={cancelarMfa}>
          Volver
        </Button>
      </div>
    </form>
  );
}

function FormOlvido({ onVolver }: { onVolver: () => void }) {
  const form = useForm<{ login: string }>({ resolver: zodResolver(z.object({ login: z.string().trim().min(3, 'Ingresa tu usuario') })), defaultValues: { login: '' } });
  const [enviado, setEnviado] = useState(false);
  const enviar = form.handleSubmit(async (d) => {
    try {
      await api.auth.pedirReset(d.login);
    } catch {
      /* no se revela si el usuario existe */
    }
    setEnviado(true);
  });
  return enviado ? (
    <>
      <Alert tono="exito">Si el usuario existe, enviamos un enlace de un solo uso al correo registrado.</Alert>
      <Button onClick={onVolver}>Volver al ingreso</Button>
    </>
  ) : (
    <form onSubmit={enviar} noValidate>
      <TextField label="Usuario" autoComplete="username" required error={form.formState.errors.login?.message} {...form.register('login')} />
      <div className="t-acciones">
        <Button type="submit" variante="primario" cargando={form.formState.isSubmitting}>
          Enviar enlace
        </Button>
        <Button variante="fantasma" onClick={onVolver}>
          Volver
        </Button>
      </div>
    </form>
  );
}

/** Enlace a «Crear cuenta» si el servidor tiene el registro público habilitado. */
function EnlaceRegistro() {
  const q = useQuery({ queryKey: ['auth', 'registro'], queryFn: api.auth.estadoRegistro, retry: false, staleTime: 5 * 60_000 });
  if (!q.data?.habilitado) return null;
  // Enlace simple (recarga completa): esta pantalla puede montarse fuera del enrutador.
  return (
    <p className="w-login__alta">
      ¿Primera vez? <a href={`${import.meta.env.BASE_URL}registro`}>Crear cuenta</a>
    </p>
  );
}

export function LoginPage() {
  const { estado, marca } = useAuth();
  const [olvido, setOlvido] = useState(false);
  const titulo = estado === 'mfa' ? 'Verificación en dos pasos' : olvido ? 'Restablecer contraseña' : 'Ingresar';
  return (
    <main className="w-login">
      <div className="w-login__caja">
        <div className="w-login__marca">{marca.nombre_producto ?? 'Tarja'}</div>
        <Card titulo={titulo}>
          {estado === 'mfa' ? <FormMfa /> : olvido ? <FormOlvido onVolver={() => setOlvido(false)} /> : <FormLogin onOlvido={() => setOlvido(true)} />}
        </Card>
        {estado !== 'mfa' && !olvido ? <EnlaceRegistro /> : null}
      </div>
    </main>
  );
}
